// "Load truck" for a delivery order (Shipping hub, Ready to ship; Phases 2-4, 2026-10-28, behind GET /features
// shipping_signing.delivery). Who drives, which truck, when it leaves; then each pallet is checked off as it goes on
// the truck (the load-out checklist: scan, typed number or tick); then ONE request ships them with the load:
//   POST /packaging/plans/{plan_id}/ship-all-ready {pin (Working-as), ship_date, notify_customer_per_pallet: false,
//        pallet_ids (the ones checked off: a split delivery ships only those), load_checklist,
//        load: {client_request_id, driver_user_id | driver_name, vehicle_id | vehicle_text, leaving_at, sign: 'delivery'}}
//   -> load: {load_id, manifest_url, sign: {request_id, url, short_code, qr_svg, expires_at}}
// The driver's copy (the manifest with the signing QR) opens to print; the QR is shown too. The order moves to Out for
// delivery; when the receiver signs on any phone it turns Delivered.
import React, { useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { postWithInhouseOverride } from '../salesOrders/palletActions';
import { detailToString } from '../../utils/apiError';
import { batchEmailNote } from '../../utils/emailOutcome';
import { shopToday } from '../../utils/shopDate';
import { liveSession } from '../operatorSession/session';
import { absoluteUrl, newRequestId, qrImageSrc } from '../signing/signRequestsApi';
import { lanePallets, palletName } from './soBoard';
import {
  DriverPicker, EMPTY_PICK, TruckPicker, driverFields, driverName, truckFields, truckName, useDrivers, useVehicles,
} from './loadPickers';

const styles = {
  big: { minHeight: 56, fontSize: '1.15rem', fontWeight: 600 },
  btn: { minHeight: 48, fontSize: '1.02rem' },
  input: { minHeight: 48, fontSize: '1.05rem', maxWidth: 200 },
  qr: { width: 220, height: 220, background: '#fff', padding: 6, borderRadius: 8, border: '1px solid #dee2e6' },
};

async function loadOut(args) {
  const { loadOutChecklist } = await import('../pallets/planPalletFlows');
  return loadOutChecklist(args);
}

function nowHHMM(d = new Date()) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "07:30" -> today's local time as ISO; '' -> null. */
export function leavingAtIso(hhmm, today = new Date()) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return null;
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate(), Number(m[1]), Number(m[2]), 0, 0);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Props: apiBase, ui (Working-as; or the package guide's ui, which asks for a PIN; or the phone's phoneUi), order (a
 *  board row in the Ready lane, or deliverySigning.loadTruckOrderFromPlan(plan, palletIds) from the package guide or
 *  the phone's sales order), onClose({loaded, manifest}), onOpenGuide(). A ui with no openPdfTab (a phone: no new tab
 *  after an await) gets no Print button: onClose hands the screen the manifest path to offer. */
export default function LoadTruckModal({ apiBase, ui, order, onClose, onOpenGuide = null }) {
  const { vehicles } = useVehicles(apiBase);
  const { drivers } = useDrivers(apiBase);
  const [driver, setDriver] = useState(EMPTY_PICK);
  const [truck, setTruck] = useState(EMPTY_PICK);
  const [leaving, setLeaving] = useState(() => nowHHMM());
  const [error, setError] = useState('');
  const [hidden, setHidden] = useState(false); // while the checklist / PIN / override questions are up
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {body, load, count, truck, driver, by}
  const requestId = useRef(newRequestId()); // one per open: a retry after a dropped answer is the same load

  const pallets = useMemo(() => lanePallets(order, 'ready'), [order]);
  const readyIds = pallets.map((p) => Number(p.pallet_id));
  const pieces = pallets.reduce((t, p) => t + Number(p.pieces || 0), 0);
  const who = (liveSession() || {}).name || null;

  async function markLoaded() {
    setError('');
    const dFields = driverFields(driver);
    const tFields = truckFields(truck);
    if (!dFields) { setError('Pick the driver (or type who is driving).'); return; }
    if (!tFields) { setError('Pick the truck (or type it under Other).'); return; }
    if (!readyIds.length) { setError('No sealed pallet is ready on this order.'); return; }

    setHidden(true);
    let lo;
    try {
      lo = await loadOut({
        planId: order.plan_id, step: 'ship', palletIds: readyIds, requireAll: false, recordLabel: 'Loaded on the truck',
      });
    } catch (_) {
      lo = { items: null };
    }
    if (!lo || lo.cancelled) { setHidden(false); return; }
    const items = Array.isArray(lo.items) ? lo.items : null;
    const ids = items ? [...new Set(items.map((it) => Number(it.pallet_id)))] : readyIds;
    if (!ids.length) { setHidden(false); setError('No pallet was checked off as loaded. Nothing shipped.'); return; }
    if (ids.length < readyIds.length) {
      const rest = readyIds.length - ids.length;
      const ok = await ui.confirm(`Ship only the ${plural(ids.length, 'pallet')} checked off (split delivery)?\n\n`
        + `The other ${plural(rest, 'pallet')} stay in Ready to ship.`);
      if (!ok) { setHidden(false); return; }
    }
    // the hub's Working-as session; the SO page's package guide (no session) asks for the PIN
    const pin = typeof ui.identity === 'function'
      ? await ui.identity('load')
      : await ui.pin(`Enter your PIN to load ${order.order_number} on the truck.`, { autofill: false });
    if (!pin) { setHidden(false); return; }

    setBusy(true);
    let res;
    try {
      res = await postWithInhouseOverride(`/packaging/plans/${order.plan_id}/ship-all-ready`, {
        pin,
        ship_date: shopToday(),
        notify_customer_per_pallet: false,
        pallet_ids: ids,
        ...(items ? { load_checklist: items } : {}),
        load: {
          client_request_id: requestId.current,
          ...dFields,
          carrier_name: null,
          ...tFields,
          leaving_at: leavingAtIso(leaving),
          sign: 'delivery',
        },
      }, ui, 'ship');
    } catch (_) {
      setBusy(false); setHidden(false);
      setError('The connection dropped before JobQ answered. Look in Out for delivery: if the order is not there, '
        + 'press Mark loaded again (the same load is never recorded twice).');
      return;
    }
    let body = null;
    try { body = await res.json(); } catch (_) { body = null; }
    setBusy(false); setHidden(false);
    if (!res.ok) {
      setError(`Not loaded: ${detailToString(body && body.detail) || `HTTP ${res.status}`}`);
      return;
    }
    const load = (body && body.load) || null;
    const manifest = (load && load.manifest_url) || (body && body.batch_manifest_url) || null;
    const by = (liveSession() || {}).name || who;
    setDone({ body: body || {}, load, manifest, count: ids.length, truck: truckName(truck, vehicles), driver: driverName(driver, drivers), by });
    if (manifest && ui.openPdfTab) {
      try { await ui.openPdfTab(absoluteUrl(apiBase, manifest), `Manifest_${order.order_number}_${shopToday()}.pdf`); } catch (_) { /* the button offers it again */ }
    }
  }

  async function printManifest() {
    if (!done || !done.manifest) return;
    try {
      await ui.openPdfTab(absoluteUrl(apiBase, done.manifest), `Manifest_${order.order_number}_${shopToday()}.pdf`);
    } catch (e) {
      setError(`Couldn't open the manifest: ${e.message || e}`);
    }
  }

  function renderDone() {
    const sign = done.load && done.load.sign;
    const qr = sign && qrImageSrc(sign.qr_svg);
    const email = batchEmailNote(done.body, 'manifest');
    const time = new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return (
      <div data-testid="load-truck-done">
        <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>Loaded · {time}</div>
        <p className="mt-2">
          {plural(done.count, 'pallet')}{done.truck ? ` on ${done.truck}` : ''}{done.by ? ` by ${done.by}` : ''}.
          {done.driver ? ` Driver ${done.driver}.` : ''} Now in Out for delivery.
        </p>
        {qr && (
          <div className="d-flex flex-wrap gap-3 align-items-center mb-3" data-testid="load-truck-qr">
            <img src={qr} alt="Delivery signing QR code" style={styles.qr} />
            <div style={{ maxWidth: 420 }}>
              {sign.short_code && <div className="mb-1">Code <strong style={{ fontFamily: 'monospace', fontSize: '1.3rem' }}>{sign.short_code}</strong></div>}
              Printed on the manifest. At the dock anyone scans it with a phone camera (the driver's or the receiver's, no
              app), the receiver signs, and the order turns Delivered.
            </div>
          </div>
        )}
        {!qr && done.load && done.load.sign_error && (
          // The truck still shipped and the load is recorded; only the signing QR couldn't be made.
          <div className="alert alert-warning py-2" data-testid="load-truck-sign-error">
            {done.load.sign_error}
          </div>
        )}
        {email && <div className="small text-muted mb-2">{email}</div>}
        {done.body.plan_completed && (
          <div className="alert alert-info py-2">
            Every pallet on {order.order_number} has shipped. Finalize the order from its package guide.
          </div>
        )}
        <div className="d-flex flex-wrap gap-2">
          {done.manifest && ui.openPdfTab && (
            <button type="button" className="btn btn-primary" style={styles.big} onClick={printManifest}>
              Print manifest (driver + customer copy)
            </button>
          )}
          {done.body.plan_completed && onOpenGuide && (
            <button type="button" className="btn btn-outline-primary" style={styles.big} onClick={() => { onClose({ loaded: true }); onOpenGuide(); }}>
              Open package guide
            </button>
          )}
          <button type="button" className="btn btn-outline-secondary" style={styles.big}
            onClick={() => onClose({ loaded: true, manifest: done.manifest || null })}>
            Done
          </button>
        </div>
      </div>
    );
  }

  function renderForm() {
    return (
      <div data-testid="load-truck-form">
        <div className="mb-3">
          <div className="text-muted">
            {plural(pallets.length, 'sealed pallet')}{pieces ? ` · ${pieces} pcs` : ''} · delivery on our truck
          </div>
          <div>Loading as <strong>{who || 'whoever signs in next'}</strong></div>
        </div>
        <div className="row g-3 mb-3">
          <div className="col-md-5">
            <DriverPicker id="load-truck-driver" drivers={drivers} value={driver} onChange={setDriver} required />
          </div>
          <div className="col-md-5">
            <TruckPicker id="load-truck-vehicle" vehicles={vehicles} value={truck} onChange={setTruck} required />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-semibold" htmlFor="load-truck-leaving">Leaving</label>
            <input id="load-truck-leaving" type="time" className="form-control" style={styles.input}
              value={leaving} onChange={(e) => setLeaving(e.target.value)} />
          </div>
        </div>
        <div className="fw-semibold mb-1">Put each pallet on the truck</div>
        <ul className="list-group mb-2" data-testid="load-truck-pallets">
          {pallets.map((p) => (
            <li key={p.pallet_id} className="list-group-item d-flex flex-wrap gap-3 align-items-center" style={{ minHeight: 48 }}>
              <strong>{palletName(p)}</strong>
              <span className="text-muted">{p.beacon || 'no beacon'}</span>
              {p.pieces ? <span>{p.pieces} pcs</span> : null}
            </li>
          ))}
        </ul>
        <div className="small text-muted mb-3">
          Next, each pallet is checked off as it goes on: a scan, a typed number or a tick all count, and the record keeps
          which one you used and who you are. Check off only some to ship a split delivery. Marking loaded ships the pallets
          in JobQ, frees their beacons, and prints the manifest with its signing QR.
        </div>
        <button type="button" className="btn btn-primary w-100" style={styles.big} disabled={busy} onClick={markLoaded}>
          {busy && <span className="spinner-border spinner-border-sm me-2" />}
          Mark loaded{who ? ` as ${who}` : ''}
        </button>
      </div>
    );
  }

  return (
    <Modal show={!hidden} size="lg" fullscreen="md-down" backdrop="static" keyboard={!busy}
      onHide={() => { if (!busy) onClose({ loaded: !!done, manifest: (done && done.manifest) || null }); }}
      aria-labelledby="load-truck-title">
      <Modal.Header closeButton={!busy}>
        <Modal.Title id="load-truck-title">
          Load truck · {order.order_number}{order.customer_name ? ` · ${order.customer_name}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {done ? renderDone() : renderForm()}
      </Modal.Body>
    </Modal>
  );
}
